import { useEffect, useRef, useState } from 'react'

import {
  downloadFileDocumentById,
  getDomondaResult,
  getDomondaSettings,
  parseDomondaDocument,
} from '../../../../api'
import { useNotification, useOutboundUuid } from '../../../../hooks'
import { getInvoiceFileDownloadParams } from '../../InvoiceModal.utils'

import { checkDomondaResultStatus } from './useInvoiceModal.utils'

export function useInvoiceModal({
  documentId,
  requestId,
  savedDocumentId = null,
}) {
  const { handleAddErrorNotification, handleAddInfoNotification } =
    useNotification()

  const outboundUuid = useOutboundUuid()

  const [pdfUrl, setPdfUrl] = useState(null)
  const [parsedValues, setParsedValues] = useState(null)

  const [isPdfLoading, setPdfLoading] = useState(Boolean(documentId))
  const [hasDomondaKey, setHasDomondaKey] = useState(false)
  const [isParsing, setParsing] = useState(false)
  const [hasParsingError, setParsingError] = useState(false)
  const [loadedDocumentId, setLoadedDocumentId] = useState(documentId)

  // Per-run cancellation token: replacing or removing the file must cancel
  // the polling started for the previous file, not the freshly started one.
  const cancellationRef = useRef({ isCancelled: false })

  function handleParse() {
    const cancellation = cancellationRef.current

    setParsing(true)
    setParsingError(false)

    handleAddInfoNotification('DocumentMarkAsInvoice.ParsingInProgressKey')

    function handleParsingSuccess(result) {
      if (!cancellation.isCancelled) {
        setParsing(false)
        setParsedValues(result ?? null)
      }
    }

    function handleParsingError() {
      if (!cancellation.isCancelled) {
        setParsing(false)
        setParsingError(true)
        handleAddErrorNotification('DocumentMarkAsInvoice.ParsingFailedKey')
      }
    }

    parseDomondaDocument({ invoice_file: documentId })
      .then(({ data }) => {
        if (data?.event_id) {
          checkDomondaResultStatus(data.event_id, {
            getResult: getDomondaResult,
            isCancelled: () => cancellation.isCancelled,
            onError: handleParsingError,
            onSuccess: handleParsingSuccess,
          })
        } else {
          handleParsingError()
        }
      })
      .catch(handleParsingError)
  }

  if (loadedDocumentId !== documentId) {
    setLoadedDocumentId(documentId)
    setPdfUrl(null)
    setPdfLoading(Boolean(documentId))
    setParsedValues(null)
    setParsing(false)
    setParsingError(false)
  }

  useEffect(() => {
    const cancellation = { isCancelled: false }

    cancellationRef.current = cancellation

    const downloadPromise = documentId
      ? downloadFileDocumentById({
          id: documentId,
          outboundUuid,
          params: getInvoiceFileDownloadParams(requestId),
        })
      : Promise.resolve(null)

    Promise.all([downloadPromise, getDomondaSettings()])
      .then(([downloadRes, settingsRes]) => {
        if (!cancellation.isCancelled) {
          setPdfUrl(downloadRes?.data ?? null)
          setPdfLoading(false)

          if (settingsRes?.data?.domonda_api_key) {
            setHasDomondaKey(true)

            // The file already saved on the invoice must not be re-parsed —
            // parsed values would overwrite the saved ones. Only freshly
            // picked files are parsed automatically.
            if (documentId && documentId !== savedDocumentId) {
              handleParse()
            }
          }
        }
      })
      .catch(() => {
        if (!cancellation.isCancelled) {
          setPdfLoading(false)
        }
      })

    return () => {
      cancellation.isCancelled = true
    }
  }, [documentId, outboundUuid, requestId])

  return {
    handleParse,
    hasDomondaKey,
    hasParsingError,
    isParsing,
    isPdfLoading,
    parsedValues,
    pdfUrl,
  }
}
